import React, { memo } from 'react';
import { FileText, CheckCircle2, AlertTriangle, Scale, Mail, Phone } from 'lucide-react';
import { SEOSchema } from '../components/SEOSchema';
import { useLanguage } from '../contexts/LanguageContext';
export function TermsOfServicePage() {
  const {
    t
  } = useLanguage();
  return <div className="pt-20 min-h-screen bg-white" data-id="element-1082">
      <SEOSchema type="about" title={t('Syarat & Ketentuan Layanan | PT Graha Riau Gemilang', 'Terms of Service | PT Graha Riau Gemilang')} description={t('Syarat dan ketentuan penggunaan layanan PT Graha Riau Gemilang untuk pembelian dan konsultasi properti.', 'Terms and conditions for using PT Graha Riau Gemilang services for property purchase and consultation.')} breadcrumbs={[{
      name: t('Beranda', 'Home'),
      url: 'https://grahariaugemilang.com/'
    }, {
      name: t('Syarat & Ketentuan', 'Terms of Service'),
      url: 'https://grahariaugemilang.com/syarat-ketentuan'
    }]} data-id="element-1083" />

      {/* Header */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-20" data-id="element-1084">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1085">
          <div className="inline-block p-3 bg-white/10 rounded-full mb-6" data-id="element-1086">
            <FileText className="w-8 h-8" data-id="element-1087" />
          </div>
          <h1 className="text-4xl font-bold mb-4" data-id="element-1088">
            {t('Syarat & Ketentuan Layanan', 'Terms of Service')}
          </h1>
          <p className="text-xl text-gray-300" data-id="element-1089">
            {t('Ketentuan penggunaan layanan PT Graha Riau Gemilang', 'Terms and conditions for using PT Graha Riau Gemilang services')}
          </p>
          <p className="text-sm text-gray-400 mt-4" data-id="element-1090">
            {t('Terakhir diperbarui: Desember 2024', 'Last updated: December 2024')}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-1091">
        <div className="prose prose-lg max-w-none" data-id="element-1092">
          {/* Introduction */}
          <section className="mb-12" data-id="element-1093">
            <div className="flex items-center mb-4" data-id="element-1094">
              <FileText className="w-6 h-6 text-[#C4161C] mr-3" data-id="element-1095" />
              <h2 className="text-2xl font-bold text-gray-900 m-0" data-id="element-1096">
                {t('Pendahuluan', 'Introduction')}
              </h2>
            </div>
            <p className="text-gray-600 leading-relaxed" data-id="element-1097">
              {t('Selamat datang di PT Graha Riau Gemilang. Syarat dan Ketentuan Layanan ini mengatur penggunaan website kami dan layanan yang kami tawarkan. Dengan mengakses atau menggunakan layanan kami, Anda menyetujui untuk terikat dengan syarat dan ketentuan ini.', 'Welcome to PT Graha Riau Gemilang. These Terms of Service govern your use of our website and the services we offer. By accessing or using our services, you agree to be bound by these terms and conditions.')}
            </p>
          </section>

          {/* Acceptance of Terms */}
          <section className="mb-12" data-id="element-1098">
            <div className="flex items-center mb-4" data-id="element-1099">
              <CheckCircle2 className="w-6 h-6 text-[#C4161C] mr-3" data-id="element-1100" />
              <h2 className="text-2xl font-bold text-gray-900 m-0" data-id="element-1101">
                {t('Penerimaan Syarat', 'Acceptance of Terms')}
              </h2>
            </div>
            <p className="text-gray-600 leading-relaxed mb-4" data-id="element-1102">
              {t('Dengan menggunakan layanan kami, Anda menyatakan bahwa:', 'By using our services, you represent that:')}
            </p>
            <ul className="space-y-2 text-gray-600" data-id="element-1103">
              <li data-id="element-1104">
                {t('Anda berusia minimal 18 tahun atau sudah menikah', 'You are at least 18 years old or married')}
              </li>
              <li data-id="element-1105">
                {t('Anda memiliki kapasitas hukum untuk mengikat kontrak', 'You have the legal capacity to enter into contracts')}
              </li>
              <li data-id="element-1106">
                {t('Informasi yang Anda berikan adalah akurat dan lengkap', 'The information you provide is accurate and complete')}
              </li>
              <li data-id="element-1107">
                {t('Anda akan mematuhi semua hukum dan peraturan yang berlaku', 'You will comply with all applicable laws and regulations')}
              </li>
            </ul>
          </section>

          {/* Services Description */}
          <section className="mb-12" data-id="element-1108">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1109">
              {t('Deskripsi Layanan', 'Services Description')}
            </h2>
            <p className="text-gray-600 mb-4" data-id="element-1110">
              {t('PT Graha Riau Gemilang menyediakan layanan berikut:', 'PT Graha Riau Gemilang provides the following services:')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1111">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-1112">
                <h4 className="font-bold text-gray-900 mb-2" data-id="element-1113">
                  {t('Pengembangan Properti', 'Property Development')}
                </h4>
                <p className="text-sm text-gray-600" data-id="element-1114">
                  {t('Pembangunan dan penjualan properti residensial dan komersial', 'Construction and sale of residential and commercial properties')}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-1115">
                <h4 className="font-bold text-gray-900 mb-2" data-id="element-1116">
                  {t('Konsultasi Properti', 'Property Consultation')}
                </h4>
                <p className="text-sm text-gray-600" data-id="element-1117">
                  {t('Layanan konsultasi pembelian dan investasi properti', 'Property purchase and investment consultation services')}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-1118">
                <h4 className="font-bold text-gray-900 mb-2" data-id="element-1119">
                  {t('Fasilitasi KPR', 'Mortgage Facilitation')}
                </h4>
                <p className="text-sm text-gray-600" data-id="element-1120">
                  {t('Bantuan proses pengajuan kredit pemilikan rumah', 'Assistance with mortgage application processes')}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-1121">
                <h4 className="font-bold text-gray-900 mb-2" data-id="element-1122">
                  {t('Layanan Purna Jual', 'After-Sales Service')}
                </h4>
                <p className="text-sm text-gray-600" data-id="element-1123">
                  {t('Dukungan dan pemeliharaan pasca pembelian properti', 'Post-purchase property support and maintenance')}
                </p>
              </div>
            </div>
          </section>

          {/* User Obligations */}
          <section className="mb-12" data-id="element-1124">
            <div className="flex items-center mb-4" data-id="element-1125">
              <AlertTriangle className="w-6 h-6 text-[#C4161C] mr-3" data-id="element-1126" />
              <h2 className="text-2xl font-bold text-gray-900 m-0" data-id="element-1127">
                {t('Kewajiban Pengguna', 'User Obligations')}
              </h2>
            </div>
            <p className="text-gray-600 mb-4" data-id="element-1128">
              {t('Sebagai pengguna layanan kami, Anda setuju untuk:', 'As a user of our services, you agree to:')}
            </p>
            <ul className="space-y-2 text-gray-600" data-id="element-1129">
              <li data-id="element-1130">
                {t('Memberikan informasi yang akurat dan terkini', 'Provide accurate and up-to-date information')}
              </li>
              <li data-id="element-1131">
                {t('Menjaga kerahasiaan informasi akun Anda (jika ada)', 'Maintain the confidentiality of your account information (if any)')}
              </li>
              <li data-id="element-1132">
                {t('Tidak menggunakan layanan untuk tujuan ilegal atau penipuan', 'Not use the services for illegal or fraudulent purposes')}
              </li>
              <li data-id="element-1133">
                {t('Membaca dan memahami semua dokumen transaksi sebelum menandatangani', 'Read and understand all transaction documents before signing')}
              </li>
              <li data-id="element-1134">
                {t('Memenuhi kewajiban pembayaran sesuai kesepakatan', 'Fulfill payment obligations as agreed')}
              </li>
              <li data-id="element-1135">
                {t('Menghormati hak kekayaan intelektual kami', 'Respect our intellectual property rights')}
              </li>
            </ul>
          </section>

          {/* Property Purchase Terms */}
          <section className="mb-12" data-id="element-1136">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1137">
              {t('Ketentuan Pembelian Properti', 'Property Purchase Terms')}
            </h2>
            <div className="space-y-4" data-id="element-1138">
              <div data-id="element-1139">
                <h4 className="font-bold text-gray-900 mb-2" data-id="element-1140">
                  {t('Booking Fee & Uang Muka', 'Booking Fee & Down Payment')}
                </h4>
                <p className="text-gray-600" data-id="element-1141">
                  {t('Booking fee bersifat non-refundable kecuali dalam kondisi tertentu yang diatur dalam perjanjian. Uang muka akan dikembalikan sesuai ketentuan jika transaksi dibatalkan oleh developer.', 'Booking fees are non-refundable except under certain conditions specified in the agreement. Down payments will be refunded according to terms if the transaction is canceled by the developer.')}
                </p>
              </div>
              <div data-id="element-1142">
                <h4 className="font-bold text-gray-900 mb-2" data-id="element-1143">
                  {t('Pembatalan oleh Pembeli', 'Cancellation by Buyer')}
                </h4>
                <p className="text-gray-600" data-id="element-1144">
                  {t('Pembatalan oleh pembeli akan dikenakan biaya administrasi sesuai dengan tahapan pembangunan dan ketentuan dalam Perjanjian Pengikatan Jual Beli (PPJB).', 'Cancellation by the buyer will be subject to administrative fees according to construction stages and terms in the Sale and Purchase Binding Agreement (PPJB).')}
                </p>
              </div>
              <div data-id="element-1145">
                <h4 className="font-bold text-gray-900 mb-2" data-id="element-1146">
                  {t('Serah Terima Unit', 'Unit Handover')}
                </h4>
                <p className="text-gray-600" data-id="element-1147">
                  {t('Serah terima unit dilakukan setelah pembayaran lunas dan dokumen lengkap. Keterlambatan serah terima akan dikompensasi sesuai ketentuan PPJB.', 'Unit handover is conducted after full payment and complete documentation. Handover delays will be compensated according to PPJB terms.')}
                </p>
              </div>
            </div>
          </section>

          {/* Intellectual Property */}
          <section className="mb-12" data-id="element-1148">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1149">
              {t('Hak Kekayaan Intelektual', 'Intellectual Property')}
            </h2>
            <p className="text-gray-600 leading-relaxed" data-id="element-1150">
              {t('Semua konten di website ini, termasuk teks, gambar, logo, desain, dan materi lainnya adalah milik PT Graha Riau Gemilang dan dilindungi oleh hukum hak cipta. Anda tidak diperkenankan untuk menyalin, memodifikasi, atau mendistribusikan konten tanpa izin tertulis dari kami.', 'All content on this website, including text, images, logos, designs, and other materials are owned by PT Graha Riau Gemilang and protected by copyright law. You may not copy, modify, or distribute content without our written permission.')}
            </p>
          </section>

          {/* Limitations of Liability */}
          <section className="mb-12" data-id="element-1151">
            <div className="flex items-center mb-4" data-id="element-1152">
              <Scale className="w-6 h-6 text-[#C4161C] mr-3" data-id="element-1153" />
              <h2 className="text-2xl font-bold text-gray-900 m-0" data-id="element-1154">
                {t('Batasan Tanggung Jawab', 'Limitations of Liability')}
              </h2>
            </div>
            <p className="text-gray-600 mb-4" data-id="element-1155">
              {t('PT Graha Riau Gemilang tidak bertanggung jawab atas:', 'PT Graha Riau Gemilang is not liable for:')}
            </p>
            <ul className="space-y-2 text-gray-600" data-id="element-1156">
              <li data-id="element-1157">
                {t('Kerugian tidak langsung atau konsekuensial dari penggunaan layanan', 'Indirect or consequential losses from service use')}
              </li>
              <li data-id="element-1158">
                {t('Gangguan layanan karena force majeure (bencana alam, perang, dll)', 'Service interruptions due to force majeure (natural disasters, war, etc.)')}
              </li>
              <li data-id="element-1159">
                {t('Kesalahan informasi yang diberikan oleh pihak ketiga', 'Misinformation provided by third parties')}
              </li>
              <li data-id="element-1160">
                {t('Keputusan investasi yang dibuat berdasarkan informasi di website', 'Investment decisions made based on website information')}
              </li>
            </ul>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4" data-id="element-1161">
              <p className="text-sm text-gray-700" data-id="element-1162">
                <strong data-id="element-1163">{t('Catatan:', 'Note:')}</strong>{' '}
                {t('Batasan ini tidak mengurangi hak-hak konsumen yang dijamin oleh hukum Indonesia.', 'These limitations do not reduce consumer rights guaranteed by Indonesian law.')}
              </p>
            </div>
          </section>

          {/* Dispute Resolution */}
          <section className="mb-12" data-id="element-1164">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1165">
              {t('Penyelesaian Sengketa', 'Dispute Resolution')}
            </h2>
            <p className="text-gray-600 leading-relaxed" data-id="element-1166">
              {t('Setiap sengketa yang timbul dari atau terkait dengan Syarat dan Ketentuan ini akan diselesaikan melalui musyawarah. Jika musyawarah tidak tercapai, sengketa akan diselesaikan melalui Pengadilan Negeri Pekanbaru sesuai dengan hukum Indonesia yang berlaku.', 'Any disputes arising from or related to these Terms and Conditions will be resolved through deliberation. If deliberation fails, disputes will be resolved through the Pekanbaru District Court in accordance with applicable Indonesian law.')}
            </p>
          </section>

          {/* Governing Law */}
          <section className="mb-12" data-id="element-1167">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1168">
              {t('Hukum yang Berlaku', 'Governing Law')}
            </h2>
            <p className="text-gray-600 leading-relaxed" data-id="element-1169">
              {t('Syarat dan Ketentuan ini diatur dan ditafsirkan sesuai dengan hukum Republik Indonesia. Semua transaksi properti tunduk pada peraturan perundang-undangan yang berlaku di Indonesia, termasuk namun tidak terbatas pada Undang-Undang Perlindungan Konsumen dan peraturan properti.', 'These Terms and Conditions are governed by and construed in accordance with the laws of the Republic of Indonesia. All property transactions are subject to applicable Indonesian laws and regulations, including but not limited to Consumer Protection Law and property regulations.')}
            </p>
          </section>

          {/* Changes to Terms */}
          <section className="mb-12" data-id="element-1170">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1171">
              {t('Perubahan Syarat & Ketentuan', 'Changes to Terms')}
            </h2>
            <p className="text-gray-600 leading-relaxed" data-id="element-1172">
              {t('Kami berhak untuk mengubah Syarat dan Ketentuan ini sewaktu-waktu. Perubahan akan dipublikasikan di halaman ini dengan tanggal "Terakhir diperbarui" yang baru. Penggunaan layanan kami setelah perubahan berarti Anda menerima syarat dan ketentuan yang telah diperbarui.', 'We reserve the right to modify these Terms and Conditions at any time. Changes will be published on this page with a new "Last updated" date. Your continued use of our services after changes means you accept the updated terms and conditions.')}
            </p>
          </section>

          {/* Contact */}
          <section className="mb-12" data-id="element-1173">
            <div className="bg-gray-50 rounded-xl p-8 border border-gray-200" data-id="element-1174">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1175">
                {t('Hubungi Kami', 'Contact Us')}
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-1176">
                {t('Jika Anda memiliki pertanyaan tentang Syarat dan Ketentuan ini, silakan hubungi kami:', 'If you have questions about these Terms and Conditions, please contact us:')}
              </p>
              <div className="space-y-3" data-id="element-1177">
                <div className="flex items-center text-gray-700" data-id="element-1178">
                  <Mail className="w-5 h-5 text-[#C4161C] mr-3" data-id="element-1179" />
                  <a href="mailto:grahariaugemilang@gmail.com" className="hover:text-[#C4161C]" data-id="element-1180">
                    grahariaugemilang@gmail.com
                  </a>
                </div>
                <div className="flex items-center text-gray-700" data-id="element-1181">
                  <Phone className="w-5 h-5 text-[#C4161C] mr-3" data-id="element-1182" />
                  <span data-id="element-1183">0761-21493 / 0761-47961</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>;
}